/**
 * ManualSaveButton — explicit "Save draft" button shown in the workflow
 * sub-header for every autosave mode *except* Instant.
 *
 * In Instant mode the throttled write loop persists on every edit within
 * a few seconds, so a manual Save would be redundant and misleading. In
 * the 30s / 5min / off modes there is a meaningful window between edits
 * and the next scheduled write, so we surface Save as a fallback the user
 * can click to flush changes immediately.
 *
 * Uses `useDraftSave` directly (rather than going through `DraftModeContext`)
 * because the sub-header is rendered above `DraftModeProvider` in the tree —
 * matches the pattern used by `DraftBadge` and `PublishButton`.
 */

import { useCallback } from 'react';
import { Button, Spinner, Tooltip, makeStyles, tokens } from '@coreai/fluentui-react';
import { SaveRegular, SaveFilled } from '@fluentui/react-icons';
import { useDraftSave } from '@/hooks/useDraftSave';
import {
  getBlockingCodeIssue,
  useHasUnsavedDraftIntent,
} from '@/hooks/useIsDraftDirty';
import { useDraftModeStore, selectIsDraftMode, selectDraftStatus } from '@/stores/draftModeStore';
import { useUIPreferencesStore } from '@/stores/uiPreferencesStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { telemetryService } from '@/services/telemetryService';
import { getShortcutLabel } from '@/utils/input';

const useStyles = makeStyles({
  button: {
    minWidth: 'auto',
    columnGap: tokens.spacingHorizontalXS,
  },
});

export interface ManualSaveButtonProps {
  /** Workflow name to save. When omitted, the button is hidden. */
  workflowName?: string;
}

export const ManualSaveButton: React.FC<ManualSaveButtonProps> = ({ workflowName }) => {
  // Subscribe to autosave mode so we hide ourselves in Instant mode,
  // where the throttled write loop already persists on every edit.
  const draftAutosaveMode = useUIPreferencesStore((s) => s.draftAutosaveMode);
  const isDraftMode = useDraftModeStore(selectIsDraftMode);

  const isVisible = draftAutosaveMode !== 'instant' && isDraftMode && !!workflowName;

  // Mount the inner component (which calls `useDraftSave` → React Query)
  // only when the button is actually visible. This keeps the button out of
  // the React tree — and out of dirty-state subscriptions / mutation hooks —
  // for the vast majority of sessions where autosave is on, and avoids
  // requiring a `QueryClientProvider` in light-render tests of the host
  // sub-header.
  if (!isVisible || !workflowName) {
    return null;
  }

  return <ManualSaveButtonInner workflowName={workflowName} />;
};

const ManualSaveButtonInner: React.FC<{ workflowName: string }> = ({ workflowName }) => {
  const styles = useStyles();
  const isDirty = useHasUnsavedDraftIntent();
  const addNotification = useNotificationStore((s) => s.addNotification);
  // Subscribe to the Code panel buffer's parse-error state so Save can refuse
  // to run — and explain why — when the raw JSON view has unfixed syntax
  // errors. Without this, `useDraftSave` would happily persist the last
  // known-good workflow tree while the user's on-screen edits get silently
  // dropped on next open.
  const codeBufferParseError = useWorkflowStore((s) => s.codeBufferParseError);
  const codeBufferDirty = useWorkflowStore((s) => s.codeBufferDirty);
  const nodeCodeDraft = useNodeEditorStore((state) => state.nodeCodeDraft);
  // Also subscribe to the shared draft status so the button spins when a
  // save started via Ctrl/Cmd+S (or any other path that doesn't go through
  // this component's local `useDraftSave` instance).
  const storeDraftStatus = useDraftModeStore(selectDraftStatus);

  const { save, isSaving: isLocalSaving } = useDraftSave({ workflowName });
  const isSaving = isLocalSaving || storeDraftStatus === 'saving';

  const hasBlockingCodeIssue =
    codeBufferDirty || codeBufferParseError !== null || nodeCodeDraft.isDirty;

  const handleClick = useCallback(async () => {
    if (isSaving) return;
    if (hasBlockingCodeIssue) {
      // Manual save while the Code buffer has a parse error would persist the
      // stale workflow tree and quietly discard the user's on-screen edits.
      // Surface the error prominently instead and let them fix it first.
      const issue = getBlockingCodeIssue();
      telemetryService.trackEvent('workflow.draft.manual_save_blocked', {
        reason: issue?.reason ?? 'code_buffer_error',
      });
      addNotification({
        type: 'error',
        title: issue?.title ?? 'Fix JSON errors before saving',
        message: issue?.message ?? 'The Code view has invalid JSON.',
      });
      return;
    }
    telemetryService.trackEvent('workflow.draft.manual_save_clicked', {
      was_dirty: String(isDirty),
    });
    const result = await save();
    if (!result.success) {
      addNotification({
        type: 'error',
        title: 'Save failed',
        message: result.error ?? 'Unable to save draft.',
      });
    }
  }, [isSaving, hasBlockingCodeIssue, isDirty, save, addNotification]);

  // Stay enabled when the buffer has a parse error so the user can click and
  // see the actionable "fix JSON" toast — a silently-disabled button here is
  // the exact discoverability bug this PR fixes.
  const isDisabled = isSaving || (!isDirty && !hasBlockingCodeIssue);
  const tooltip = isSaving
    ? 'Saving…'
    : nodeCodeDraft.isDirty
      ? 'Apply or revert node code changes before saving'
      : codeBufferParseError
        ? `Fix JSON errors to save: ${codeBufferParseError}`
        : codeBufferDirty
          ? 'Wait for workflow JSON validation before saving'
      : isDirty
        ? `Save draft (${getShortcutLabel('save')})`
        : 'No unsaved changes';

  return (
    <Tooltip content={tooltip} relationship="label">
      <Button
        className={styles.button}
        appearance="secondary"
        icon={
          isSaving ? (
            <Spinner size="tiny" />
          ) : isDirty || hasBlockingCodeIssue ? (
            <SaveFilled />
          ) : (
            <SaveRegular />
          )
        }
        onClick={handleClick}
        disabled={isDisabled}
        aria-label={isSaving ? 'Saving draft' : 'Save draft'}
      >
        Save
      </Button>
    </Tooltip>
  );
};

export default ManualSaveButton;
